import moment from "moment";
import React, { FC } from "react";
import { LuBriefcase } from "react-icons/lu";

import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { Avatar } from "../../../../components/newUI/avatar";
import { Button } from "../../../../components/newUI/button";
import { DotsSvg } from "../../../../components/svg/DotsSvg";
import { renderRichText } from "../../utils/parseNote";
import { ContactNoteAction, ContactNoteViewModel } from "./Note.types";
import { noteHeading } from "./noteHeading";

type Props = {
  note: ContactNoteViewModel;
  actions: ContactNoteAction[];
  onView: () => void;
  onOpenWork?: (workId: string) => void;
};

export const NoteCard: FC<Props> = ({ note, actions, onView, onOpenWork }) => {
  const isLongNote = (note.note || "").length > 240;
  // The work chip below already names the work.
  const heading = noteHeading(note, !!(note.workId && note.workName));

  return (
    <div
      className={"rounded-xl border border-[#E6E8EE] bg-white px-5 py-[18px]"}>
      <div className={"flex items-start gap-3"}>
        <Avatar
          id={note.id}
          fullName={note.authorName}
          size={"sm"}
          color={note.authorColor}
          avatarClassNames={
            "h-[26px] w-[26px] shrink-0 text-[10.5px] font-bold"
          }
        />
        <div className={"min-w-0 flex-1"}>
          <div className={"flex items-center justify-between gap-3"}>
            <span className={"text-[12.5px] font-semibold text-[#8A92A3]"}>
              {note.authorName}
            </span>
            <span className={"flex shrink-0 items-center gap-3"}>
              {note.isPinned && (
                <span
                  className={
                    "rounded-full bg-[#F4F2FF] px-2 py-0.5 text-xs font-bold text-[#6B4EFF]"
                  }>
                  Pinned
                </span>
              )}
              <span
                className={"whitespace-nowrap text-[12.5px] text-[#9097A8]"}>
                {note.createdAt
                  ? moment(note.createdAt).format("MMM DD, YYYY")
                  : ""}
              </span>
              <SettingsDropdown actionsList={actions}>
                <Button
                  type={"button"}
                  variant={"outline"}
                  size={"sm"}
                  iconOnly
                  autoWidth
                  leftIcon={<DotsSvg />}
                  className={"shrink-0"}
                />
              </SettingsDropdown>
            </span>
          </div>
          {heading && (
            <div className={"mt-1 text-[15.5px] font-extrabold text-[#1F2433]"}>
              {heading}
            </div>
          )}
          <div
            onClick={onView}
            className={
              "rich-text-content mt-1.5 line-clamp-5 cursor-pointer overflow-hidden break-words text-[13.5px] leading-[1.55] text-[#5F6577] [&_*]:[overflow-wrap:anywhere] [&_.rich-text-content]:contents"
            }>
            {renderRichText(note.note || "")}
          </div>
          {isLongNote && (
            <Button
              type={"button"}
              variant={"ghost"}
              size={"xs"}
              autoWidth
              className={
                "mt-1.5 h-auto p-0 text-[12.5px] font-bold text-[#6B4EFF] hover:bg-transparent"
              }
              onClick={onView}>
              Read full note
            </Button>
          )}
          {note.workId && note.workName && (
            <div className={"mt-3"}>
              <Button
                type={"button"}
                variant={"ghost"}
                size={"xs"}
                autoWidth
                leftIcon={<LuBriefcase size={13} strokeWidth={2} />}
                className={
                  "h-auto rounded-[7px] border border-[#E4E7EC] bg-[#FAFAFB] px-2 py-1 text-xs font-semibold text-[#5F6577] hover:border-[#6B4EFF] hover:bg-[#F1EEFC] hover:text-[#5B41E0]"
                }
                onClick={() => {
                  if (note.workId) {
                    onOpenWork?.(note.workId);
                  }
                }}>
                {note.workName}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
